import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";
import SuiviTimeline from "./SuiviTimeline";
import EmailModal from "../../components/EmailModal";
import { useTranslation } from "react-i18next";
import { formatDate } from "utils/formatDate";
import { libelleLangue, libelleScolarite, libelleJour, libelleMatiere, libelleValeur } from "utils/valeurs";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const MATIERES_COLORS = {
  quoran: { color: "#2e7d32", bg: "#e8f5e9" },
  arabe: { color: "#1565c0", bg: "#e3f2fd" },
  science: { color: "#e65100", bg: "#fff3e0" },
};

function InfoItem({ label, value, fullWidth }) {
  if (!value && value !== false) return null;
  return (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <MDBox mb={2}>
        <MDTypography variant="caption" fontWeight="bold" sx={{
          textTransform: "uppercase", fontSize: 11,
          letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 0.5,
        }}>
          {label}
        </MDTypography>
        <MDTypography variant="body2" sx={{ color: "#344767", fontWeight: 500 }}>
          {value}
        </MDTypography>
      </MDBox>
    </Grid>
  );
}

function Section({ title, icon, children }) {
  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox sx={{
          width: 28, height: 28, borderRadius: "8px",
          background: "linear-gradient(135deg, #1565c0, #1976d2)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 14 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", fontSize: 15 }}>
          {title}
        </MDTypography>
      </MDBox>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={1}>
        {children}
      </Grid>
    </MDBox>
  );
}

function BadgeChip({ value, color = "#1565c0", bg = "#e3f2fd" }) {
  return (
    <MDBox sx={{
      display: "inline-block", padding: "4px 12px",
      borderRadius: "20px", background: bg, color,
      fontSize: 12, fontWeight: 600, mr: 0.5, mb: 0.5,
    }}>
      {value}
    </MDBox>
  );
}

function LocalisationMap({ adresse, ville, codePostal, nom, prenom, latitude, longitude }) {
  const { t } = useTranslation();
  const [coords, setCoords] = useState(null);
  const [geocodeLoading, setGeocodeLoading] = useState(true);
  const [geocodeError, setGeocodeError] = useState(false);
  const adresseComplete = [adresse, codePostal, ville].filter(Boolean).join(", ");
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAPS_API_KEY });

  useEffect(() => {
    if (latitude && longitude) {
      setCoords({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      setGeocodeLoading(false);
      return;
    }
    if (!adresseComplete) { setGeocodeLoading(false); setGeocodeError(true); return; }
    fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(adresseComplete)}&key=${GOOGLE_MAPS_API_KEY}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "OK" && data.results.length > 0) {
          const location = data.results[0].geometry.location;
          setCoords({ lat: location.lat, lng: location.lng });
        } else { setGeocodeError(true); }
      })
      .catch(() => setGeocodeError(true))
      .finally(() => setGeocodeLoading(false));
  }, [adresseComplete, latitude, longitude]);

  if (geocodeLoading || !isLoaded) return (
    <MDBox display="flex" alignItems="center" justifyContent="center"
      sx={{ height: 300, background: "#f8faff", borderRadius: "12px", border: "1px solid #e3f2fd" }}>
      <MDTypography variant="body2" color="text">{t("commun.chargementCarte")}</MDTypography>
    </MDBox>
  );

  if (geocodeError || !coords) return (
    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}
      sx={{ height: 200, background: "#f8faff", borderRadius: "12px", border: "1px solid #e3f2fd" }}>
      <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>location_off</Icon>
      <MDTypography variant="body2" color="text">{t("commun.adresse.introuvable")}</MDTypography>
      <MDTypography variant="caption" color="text">{adresseComplete || t("commun.adresse.aucune")}</MDTypography>
    </MDBox>
  );

  return (
    <MDBox sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e3f2fd" }}>
      <GoogleMap mapContainerStyle={{ height: "350px", width: "100%" }}
        center={coords} zoom={17}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}>
        <Marker position={coords} title={`${prenom} ${nom}`} />
      </GoogleMap>
      <MDBox px={2} py={1} sx={{ background: "#f8faff", borderTop: "1px solid #e3f2fd" }}>
        <MDTypography variant="caption" color="text">
          📍 {adresseComplete}
          {latitude && longitude && <span style={{ color: "#2e7d32", marginLeft: 8 }}>{t("commun.adresse.localisationPrecise")}</span>}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

function EleveDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [eleve, setEleve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModal, setEmailModal] = useState(false);
  const [livresTermines, setLivresTermines] = useState([]);
  const [absences, setAbsences] = useState([]);

  useEffect(() => {
    api.get(`/eleves/${id}`)
      .then(({ data }) => setEleve(data))
      .finally(() => setLoading(false));

    // Livres terminés
    api.get(`/suivis/eleve/${id}`).then(({ data: suivis }) => {
      if (!suivis || suivis.length === 0) return;
      const livresMap = {};
      suivis.forEach((s) => {
        if (!s.livre) return;
        const livreId = s.livre.id;
        if (!livresMap[livreId]) {
          livresMap[livreId] = { livre: s.livre, total: 0, valide: 0, derniere_date: null };
        }
        livresMap[livreId].total++;
        if (s.statut === "valide") {
          livresMap[livreId].valide++;
          const dateFinale = s.date_fin || s.date_mise_a_jour;
          if (dateFinale && (!livresMap[livreId].derniere_date || new Date(dateFinale) > new Date(livresMap[livreId].derniere_date))) {
            livresMap[livreId].derniere_date = dateFinale;
          }
        }
      });
      const termines = Object.values(livresMap)
        .filter((l) => l.total > 0 && l.valide === l.total)
        .sort((a, b) => new Date(b.derniere_date) - new Date(a.derniere_date));
      setLivresTermines(termines);
    }).catch(() => {});

    // Absences
    api.get(`/absences/eleve/${id}`).then(({ data }) => {
      setAbsences(data);
    }).catch(() => {});
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm(t("eleves.confirmerSuppression"))) return;
    try {
      await api.delete(`/eleves/${id}`);
      navigate("/eleves");
    } catch (error) {
      alert(error.response?.data?.message || t("commun.erreurSuppression"));
    }
  };

  if (loading) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}><MDTypography variant="body2" color="text">{t("commun.chargement")}</MDTypography></MDBox>
    </DashboardLayout>
  );

  if (!eleve) return (
    <DashboardLayout><DashboardNavbar />
      <MDBox pt={6}><MDTypography variant="body2" color="error">{t("eleves.introuvable")}</MDTypography></MDBox>
    </DashboardLayout>
  );

  const age = eleve.date_naissance
    ? Math.floor((new Date() - new Date(eleve.date_naissance)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={10}>

            {/* Bouton retour + actions */}
            <MDBox mb={3} display="flex" alignItems="center"
              justifyContent="space-between" flexWrap="wrap" gap={2}>
              <MDButton variant="outlined" color="secondary" size="small"
                onClick={() => navigate("/eleves")} sx={{ borderRadius: "10px", px: 2 }}>
                <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
                <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {t("commun.retourListe")}
                </MDBox>
              </MDButton>
              <MDBox display="flex" gap={1} flexWrap="wrap">
                {eleve.email && (
                  <MDButton variant="outlined" color="info" size="small"
                    onClick={() => setEmailModal(true)} sx={{ borderRadius: "10px" }}>
                    <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>email</Icon>
                    <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                      {t("commun.envoyerEmail")}
                    </MDBox>
                  </MDButton>
                )}
                <MDButton variant="outlined" color="warning" size="small"
                  onClick={() => navigate(`/eleves/${id}/modifier`)} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>edit</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {t("commun.modifier")}
                  </MDBox>
                </MDButton>
                <MDButton variant="outlined" color="error" size="small"
                  onClick={handleDelete} sx={{ borderRadius: "10px" }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>cancel</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    {t("commun.supprimer")}
                  </MDBox>
                </MDButton>
              </MDBox>
            </MDBox>

            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={{ xs: 2, sm: 3 }} sx={{
                background: "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                borderRadius: "12px", boxShadow: "0 4px 20px rgba(21,101,192,0.4)",
              }}>
                <MDBox display="flex" alignItems="center" gap={{ xs: 2, sm: 3 }}>
                  <MDBox sx={{
                    width: { xs: 70, sm: 100 }, height: { xs: 70, sm: 100 },
                    borderRadius: "50%",
                    border: "3px solid rgba(255,255,255,0.5)", overflow: "hidden", flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: "rgba(255,255,255,0.2)",
                  }}>
                    {eleve.photo ? (
                      <img src={`${eleve.photo}`} alt={`${eleve.prenom} ${eleve.nom}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Icon style={{ fontSize: 40, color: "rgba(255,255,255,0.8)" }}>person</Icon>
                    )}
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="h4" color="white" fontWeight="bold"
                      sx={{ fontSize: { xs: "1.3rem", sm: "2rem" } }}>
                      {eleve.prenom} {eleve.nom}
                    </MDTypography>
                    <MDBox display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      {eleve.email && (
                        <MDTypography variant="caption" color="white" sx={{
                          opacity: 0.85, display: { xs: "none", sm: "block" },
                        }}>
                          ✉️ <bdi dir="ltr">{eleve.email}</bdi>
                        </MDTypography>
                      )}
                      {age && (
                        <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                          · {t("eleves.ans", { count: age })}
                        </MDTypography>
                      )}
                      {eleve.sexe && (
                        <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                          · {libelleValeur("sexe", eleve.sexe)}
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>

              <MDBox pt={4} pb={4} px={{ xs: 2, sm: 4 }}>

                <Section title={t("eleves.sections.identite")} icon="person">
                  <InfoItem label={t("champs.prenom")} value={eleve.prenom} />
                  <InfoItem label={t("champs.nom")} value={eleve.nom} />
                  <InfoItem label={t("champs.dateNaissance")}
                    value={eleve.date_naissance ? formatDate(eleve.date_naissance) : null} />
                  <InfoItem label={t("champs.age")} value={age ? t("eleves.ans", { count: age }) : null} />
                  <InfoItem label={t("champs.sexe")} value={libelleValeur("sexe", eleve.sexe)} />
                  <InfoItem label={t("champs.situationFamiliale")}
                    value={libelleValeur("situation", eleve.situation_familiale)} />
                </Section>

                <Section title={t("eleves.sections.coordonnees")} icon="contact_phone">
                  <InfoItem label={t("champs.adresse")} value={eleve.adresse} fullWidth />
                  <InfoItem label={t("champs.ville")} value={eleve.ville} />
                  <InfoItem label={t("champs.codePostal")} value={eleve.code_postal} />
                  <InfoItem label={t("champs.telephone")} value={eleve.telephone} />
                  <InfoItem label={t("champs.email")} value={eleve.email} />
                </Section>

                {(eleve.langue_parlee || []).length > 0 && (
                  <Section title={t("champs.languesParlees")} icon="language">
                    <Grid item xs={12}>
                      <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                        {eleve.langue_parlee.map((l) => <BadgeChip key={l} value={libelleLangue(l)} />)}
                      </MDBox>
                    </Grid>
                  </Section>
                )}

                <Section title={t("eleves.sections.scolarite")} icon="work">
                  <InfoItem label={t("champs.niveauEtudes")} value={libelleScolarite(eleve.niveau_etudes)} />
                  <InfoItem label={t("champs.classe")} value={eleve.classe} />
                  <InfoItem label={t("eleves.filiereDiplome")} value={eleve.precision_etudes} />
                  <InfoItem label={t("champs.profession")} value={eleve.profession} />
                </Section>

                <Section title={t("eleves.sections.religieux")} icon="menu_book">
                  <InfoItem label={t("champs.niveauQuoran")}
                    value={libelleValeur("niveau", eleve.niveau_quoran)} />
                  <InfoItem label={t("champs.niveauArabe")}
                    value={libelleValeur("niveau", eleve.niveau_arabe)} />
                  <InfoItem label={t("champs.saitLireArabe")}
                    value={eleve.sait_lire_arabe === true ? `✅ ${t("commun.oui")}`
                      : eleve.sait_lire_arabe === false ? `❌ ${t("commun.non")}` : null} />
                  <InfoItem label={t("champs.ancien")}
                    value={eleve.est_ancien === true ? `✅ ${t("commun.oui")}`
                      : eleve.est_ancien === false ? `❌ ${t("commun.non")}` : null} />
                  <InfoItem label={t("champs.habachi")}
                    value={eleve.is_habachi === true ? `✅ ${t("commun.oui")}`
                      : eleve.is_habachi === false ? `❌ ${t("commun.non")}` : null} />
                  <InfoItem label={t("champs.niveauEngagement")}
                    value={libelleValeur("engagement", eleve.niveau_engagement) || null} />
                </Section>

                <Section title={t("eleves.sections.pratique")} icon="schedule">
                  {(eleve.disponibilites || []).length > 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="caption" fontWeight="bold" sx={{
                        textTransform: "uppercase", fontSize: 11,
                        letterSpacing: "0.05em", color: "#9e9e9e", display: "block", mb: 1,
                      }}>
                        {t("champs.disponibilites")}
                      </MDTypography>
                      <MDBox display="flex" flexWrap="wrap" gap={0.5} mb={2}>
                        {eleve.disponibilites.map((d) => (
                          <BadgeChip key={d} value={libelleJour(d)} color="#2e7d32" bg="#e8f5e9" />
                        ))}
                      </MDBox>
                    </Grid>
                  )}
                  <InfoItem label={t("eleveForm.modePrefere")}
                    value={libelleValeur("mode", eleve.mode_cours)} />
                </Section>

                <Section title={t("eleves.sections.complementaires")} icon="info">
                  <InfoItem label={t("champs.amenePar")} value={eleve.amene_par} />
                  <InfoItem label={t("champs.commentaires")} value={eleve.commentaires} fullWidth />
                </Section>

                {/* ── LIVRES TERMINÉS ── */}
                {livresTermines.length > 0 && (
                  <Section title={t("eleves.livresTermines", { n: livresTermines.length })} icon="verified">
                    <Grid item xs={12}>
                      <MDBox display="flex" flexWrap="wrap" gap={2}>
                        {livresTermines.map((item) => {
                          const m = MATIERES_COLORS[item.livre.matiere] || { color: "#344767", bg: "#f5f5f5" };
                          return (
                            <MDBox key={item.livre.id} sx={{
                              display: "flex", alignItems: "center", gap: 2,
                              padding: "12px 16px", borderRadius: "14px",
                              background: m.bg, border: `1px solid ${m.color}33`,
                              minWidth: 220,
                            }}>
                              <MDBox sx={{
                                width: 40, height: 56, borderRadius: "6px",
                                background: "#fff", overflow: "hidden", flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                border: `1px solid ${m.color}33`,
                              }}>
                                {item.livre.couverture ? (
                                  <img src={item.livre.couverture} alt=""
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                ) : (
                                  <Icon sx={{ color: m.color, fontSize: 22 }}>menu_book</Icon>
                                )}
                              </MDBox>
                              <MDBox flex={1}>
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767", display: "block" }}>
                                  {item.livre.nom}
                                </MDTypography>
                                <MDBox sx={{
                                  display: "inline-flex", alignItems: "center", gap: 0.5,
                                  padding: "2px 8px", borderRadius: "10px",
                                  background: m.color, color: "#fff",
                                  fontSize: 10, fontWeight: 700, mt: 0.5,
                                }}>
                                  {libelleMatiere(item.livre.matiere)}
                                </MDBox>
                                {item.derniere_date && (
                                  <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                    ✅ {t("eleves.termineLe", { date: formatDate(item.derniere_date) })}
                                  </MDTypography>
                                )}
                                <MDTypography variant="caption" sx={{ color: m.color, fontWeight: 600 }}>
                                  {t("eleves.chapitresValides", { valide: item.valide, total: item.total })}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          );
                        })}
                      </MDBox>
                    </Grid>
                  </Section>
                )}

                {/* ── ABSENCES ── */}
                <Section title={t("eleves.absencesTitre", { n: absences.length })} icon="event_busy">
                  <Grid item xs={12}>
                    {absences.length === 0 ? (
                      <MDBox display="flex" flexDirection="column" alignItems="center" py={3} gap={1}
                        sx={{ background: "#f8faff", borderRadius: "10px" }}>
                        <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>event_available</Icon>
                        <MDTypography variant="body2" color="text">
                          {t("eleves.aucuneAbsence")}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      absences.map((absence, index) => {
                        const m = MATIERES_COLORS[absence.cours?.matiere] || { color: "#c62828", bg: "#ffebee" };
                        return (
                          <MDBox key={absence.id}
                            display="flex" alignItems="flex-start" gap={2}
                            py={1.5} px={2} mb={1}
                            sx={{
                              background: index % 2 === 0 ? "#fff5f5" : "#fff",
                              borderRadius: "12px", border: "1px solid #ffcdd2",
                              cursor: "pointer", transition: "all 0.2s",
                              "&:hover": { background: "#ffebee", transform: "translateX(4px)" },
                            }}
                            onClick={() => navigate(`/cours/${absence.cours_id}`)}>
                            {/* Icône matière */}
                            <MDBox sx={{
                              width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                              background: m.bg, color: m.color,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <Icon sx={{ fontSize: 18 }}>menu_book</Icon>
                            </MDBox>

                            <MDBox flex={1}>
                              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                {/* Date */}
                                <MDBox sx={{
                                  padding: "1px 8px", borderRadius: "8px",
                                  background: "#ffebee", color: "#c62828",
                                  fontSize: 11, fontWeight: 600,
                                }}>
                                  📅 {formatDate(absence.date)}
                                </MDBox>
                                {/* Matière */}
                                {absence.cours?.matiere && (
                                  <MDBox sx={{
                                    padding: "1px 8px", borderRadius: "8px",
                                    background: m.bg, color: m.color,
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    {libelleMatiere(absence.cours.matiere)}
                                  </MDBox>
                                )}
                                {/* Rattrapage */}
                                {absence.rattrapage_prevu && (
                                  <MDBox sx={{
                                    padding: "1px 8px", borderRadius: "8px",
                                    background: "#e8f5e9", color: "#2e7d32",
                                    fontSize: 11, fontWeight: 600,
                                  }}>
                                    🔄 {t("eleves.rattrapageLe", { date: formatDate(absence.rattrapage_prevu) })}
                                  </MDBox>
                                )}
                              </MDBox>

                              {/* Enseignant */}
                              {absence.cours?.enseignant && (
                                <MDTypography variant="caption" color="text" display="block" mt={0.3}>
                                  👨‍🏫 {absence.cours.enseignant.prenom} {absence.cours.enseignant.nom}
                                </MDTypography>
                              )}
                              {/* Motif */}
                              {absence.motif && (
                                <MDTypography variant="caption" color="text" display="block" mt={0.3}>
                                  📝 {absence.motif}
                                </MDTypography>
                              )}
                              {/* Commentaire */}
                              {absence.commentaire && (
                                <MDTypography variant="caption" color="text" display="block" mt={0.3}
                                  sx={{ fontStyle: "italic" }}>
                                  {absence.commentaire}
                                </MDTypography>
                              )}
                            </MDBox>

                            <Icon sx={{ color: "#e0e0e0", fontSize: 16, mt: 0.5 }}>chevron_right</Icon>
                          </MDBox>
                        );
                      })
                    )}
                  </Grid>
                </Section>

                <Section title={t("eleves.sections.coursSuivis")} icon="menu_book">
                  <Grid item xs={12}>
                    <MDBox display="flex" justifyContent="flex-end" mb={2}>
                      <MDButton variant="outlined" color="info" size="small"
                        onClick={() => navigate(`/cours/nouveau?eleve=${id}`)}>
                        <Icon sx={{ mr: 0.5 }}>add</Icon> {t("eleves.affecterCours")}
                      </MDButton>
                    </MDBox>
                    {(eleve.cours || []).length === 0 ? (
                      <MDBox display="flex" flexDirection="column" alignItems="center" py={3} gap={1}
                        sx={{ background: "#f8faff", borderRadius: "10px" }}>
                        <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>menu_book</Icon>
                        <MDTypography variant="body2" color="text">
                          {t("eleves.aucunCours")}
                        </MDTypography>
                      </MDBox>
                    ) : (
                      eleve.cours.map((c) => {
                        const m = MATIERES_COLORS[c.matiere] || { color: "#344767", bg: "#f5f5f5" };
                        const premierLivre = c.chapitres?.[0]?.livre;
                        return (
                          <MDBox key={c.id} onClick={() => navigate(`/cours/${c.id}`)}
                            display="flex" alignItems="center" gap={2}
                            py={1.5} px={2} mb={1.5}
                            sx={{
                              background: m.bg, borderRadius: "14px",
                              border: `1px solid ${m.color}22`, cursor: "pointer",
                              transition: "all 0.2s", flexWrap: "wrap",
                              "&:hover": { boxShadow: `0 4px 16px ${m.color}22`, transform: "translateY(-2px)" },
                            }}>
                            <MDBox sx={{
                              width: 52, height: 70, borderRadius: "8px",
                              background: "#fff", flexShrink: 0, overflow: "hidden",
                              display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center",
                              border: `1px solid ${m.color}33`, boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}>
                              {premierLivre?.couverture ? (
                                <img src={`${premierLivre.couverture}`} alt={t("eleves.couverture")}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <Icon sx={{ color: m.color, fontSize: 28 }}>menu_book</Icon>
                              )}
                            </MDBox>
                            <MDBox flex={1}>
                              <MDBox display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                                <MDBox sx={{
                                  padding: "2px 10px", borderRadius: "10px",
                                  background: m.color, color: "#fff", fontSize: 11, fontWeight: 700,
                                }}>
                                  {libelleMatiere(c.matiere)}
                                </MDBox>
                                {c.enseignant && (
                                  <MDTypography variant="caption" color="text">
                                    👨‍🏫 {c.enseignant.prenom} {c.enseignant.nom}
                                  </MDTypography>
                                )}
                              </MDBox>
                              {premierLivre && (
                                <MDTypography variant="body2" fontWeight="bold"
                                  sx={{ color: "#344767", mb: 0.5 }}>
                                  📖 {premierLivre.nom}
                                </MDTypography>
                              )}
                              <MDTypography variant="caption" color="text">
                                📍 {c.lieu || "—"} · 🕐 {formatPlanning(c) || "—"}
                              </MDTypography>
                            </MDBox>
                            <MDBox>
                              {c.chapitre_termine ? (
                                <MDBox sx={{
                                  padding: "4px 12px", borderRadius: "12px",
                                  background: "#e8f5e9", color: "#2e7d32",
                                  fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                                }}>✓ {t("commun.termine")}</MDBox>
                              ) : (
                                <MDBox sx={{
                                  padding: "4px 12px", borderRadius: "12px",
                                  background: "#fff3e0", color: "#e65100",
                                  fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                                }}>⏳ {t("commun.enCours")}</MDBox>
                              )}
                            </MDBox>
                          </MDBox>
                        );
                      })
                    )}
                  </Grid>
                </Section>

                <Section title={t("eleves.sections.suiviChapitres")} icon="timeline">
                  <Grid item xs={12}>
                    <SuiviTimeline eleveId={eleve.id} />
                  </Grid>
                </Section>

                <Section title={t("eleves.sections.localisation")} icon="location_on">
                  <Grid item xs={12}>
                    <LocalisationMap adresse={eleve.adresse} ville={eleve.ville}
                      codePostal={eleve.code_postal} nom={eleve.nom} prenom={eleve.prenom}
                      latitude={eleve.latitude} longitude={eleve.longitude} />
                  </Grid>
                </Section>

              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <EmailModal
        open={emailModal}
        onClose={() => setEmailModal(false)}
        eleveId={eleve.id}
        nomDestinataire={`${eleve.prenom} ${eleve.nom}`}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default EleveDetail;